import {CommonModule} from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  input,
  Output,
  output,
  Signal,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormControl, FormRecord, ReactiveFormsModule} from '@angular/forms';
import {RouterLink} from '@angular/router';
import {Store} from '@ngrx/store';
import {of} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {SeFeButtonModule} from 'se-fe-button';
import {SeFeEmptyStateModule} from 'se-fe-empty-state';
import {SeFeFormFieldOptionsModule} from 'se-fe-form-field-options';
import {SeFeIconModule} from 'se-fe-icon';
import {SeFePaginationChange, SeFePaginationModule, SeFePaginationOptions} from 'se-fe-pagination';
import {SeFeSelectOptions} from 'se-fe-select';
import {SeFeSpinnerModule} from 'se-fe-spinner';
import {InitalSort, SeFeTableDataSource, SeFeTableModule, SeFeTableSortDirective, SortChangeEvent} from 'se-fe-table';
import {SeFeToolbarModule} from 'se-fe-toolbar';
import {ProductQueryParams, SortDirection} from '../../../models';
import {CatalogSummary, isBundleProduct, Product} from '../../../models/clare';
import {CatalogApiService} from '../../../services/catalog-api.service';
import {selectSelectedOrganizationId} from '../../../state/router/router.selectors';
import {ProductNameCellComponent} from '../product-table-cells/product-name-cell/product-name-cell.component';
import {ProductPriceCellComponent} from '../product-table-cells/product-price-cell/product-price-cell.component';
import {ProductStatusCellComponent} from '../product-table-cells/product-status-cell/product-status-cell.component';
import {ProductTableFiltersComponent} from './product-table-filters.component';
import {getTableRowId, getTableRowPrice, ProductTableRow} from './product-table-row';

export interface ProductTableSelectionChange {
  currentPageIds: string[];
  selectedRows: ProductTableRow[];
}

@Component({
  selector: 'product-table',
  templateUrl: './product-table.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    SeFeButtonModule,
    SeFeEmptyStateModule,
    SeFeFormFieldOptionsModule,
    SeFeIconModule,
    SeFePaginationModule,
    SeFeSpinnerModule,
    SeFeTableModule,
    SeFeToolbarModule,
    ProductTableFiltersComponent,
    ProductNameCellComponent,
    ProductPriceCellComponent,
    ProductStatusCellComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './product-table.component.scss',
})
export class ProductTableComponent {
  @Output() queryChanged = new EventEmitter<ProductQueryParams>();
  @Output() toggleActiveStatus = new EventEmitter<{productId: string; active: boolean}>();
  @Output() removeFromBundle = new EventEmitter<Product>();
  @Output() deleteProduct = new EventEmitter<Product>();
  @Output() removeProduct = new EventEmitter<ProductTableRow>();
  @Output() selectionChanged = new EventEmitter<ProductTableSelectionChange>();
  readonly chooseCatalogItems = output<EventTarget | null>();

  sorter = viewChild.required(SeFeTableSortDirective);

  loading = input.required();
  products = input<ProductTableRow[] | undefined>();
  paginationEnabled = input(false);
  paginationOptions = input<SeFePaginationOptions>();
  query = input<string | undefined>();
  active = input<boolean | null | undefined>();
  catalogId = input<number | null | undefined>();
  selectable = input(false);
  selectedRowIds = input<Set<string>>(new Set());
  showDelete = input(false);
  showRemove = input(false);
  showSearch = input(false);
  showToolbarFilters = input(true);
  showCatalogEmptyState = input(false);
  showNoResultsEmptyState = input(false);
  sortBy = input<string | undefined>();
  sortDirection = input<SortDirection | undefined>();
  mode = input<'bundleList' | 'catalogBundle' | 'catalogDetail' | 'productList'>('productList');
  linkPrefix = input('./');

  form = new FormRecord<FormControl<boolean>>({
    selectAll: new FormControl(false, {nonNullable: true}),
  });
  readonly selectAllOptions = [{label: 'Select all items', value: true, hiddenLabel: true}];
  readonly rowSelectionOptions = [{label: 'Select item', value: true, hiddenLabel: true}];
  readonly filterMenuOpen = signal(false);
  readonly catalogFilterCatalogCount = signal(0);
  readonly catalogFilterOptions = signal<SeFeSelectOptions>({
    sections: [],
  });

  isBundleMode = computed(() => this.mode() === 'bundleList' || this.mode() === 'catalogBundle');
  isCatalogMode = computed(() => this.mode() === 'catalogDetail' || this.mode() === 'catalogBundle');
  filterMenuTitle = computed(() => (this.isBundleMode() ? 'Bundle filters' : 'Product filters'));
  showCatalogFilter = computed(() => this.catalogFilterCatalogCount() !== 1);

  /**
   * True when the product list has a search query or filters applied.
   * @type Signal<boolean>
   */
  hasQueryOrFiltersApplied: Signal<boolean> = computed(() => {
    const active = this.active();
    const catalogId = this.catalogId();
    return !!this.query()?.trim() || typeof active === 'boolean' || typeof catalogId === 'number';
  });
  /**
   * True when the product list has no products and no search/filters applied.
   * @type Signal<boolean>
   */
  isZeroState: Signal<boolean> = computed(() => !this.products()?.length && !this.hasQueryOrFiltersApplied());
  /**
   * Product zero-state only when the product list has no products and no
   * search/filters. Filtered empties keep the toolbar so users can adjust filters.
   */
  isProductZeroState: Signal<boolean> = computed(() => this.mode() === 'productList' && this.isZeroState());
  isCatalogZeroState = computed(() => this.showCatalogEmptyState() && this.isZeroState());
  isNoResultsZeroState = computed(() => this.showNoResultsEmptyState() && this.isZeroState());
  emptyStateTitle = computed(() => {
    if (this.isBundleMode()) {
      return 'No bundles found';
    }
    if (this.isProductZeroState()) {
      return 'Create and Manage Products';
    }
    return 'No products found';
  });
  nameColumnLabel = computed(() => (this.isBundleMode() ? 'Bundle Name' : 'Product Name'));
  tableTitle = computed(() => (this.isBundleMode() ? 'Bundles table' : 'Products table'));

  displayedColumns = computed(() => {
    const columns = this.isCatalogMode() ? ['Name', 'Active', 'Price'] : ['Name', 'Price', 'Active'];
    const isProductListWithDelete = this.mode() === 'productList' && this.showDelete();

    if (this.selectable()) {
      columns.unshift('Select');
    }

    if (isProductListWithDelete || this.showRemove()) {
      columns.push('Delete');
    }

    return columns;
  });

  datasource = computed(() => {
    const prods = this.products() || [];
    return new SeFeTableDataSource(prods);
  });

  initialSort = computed((): InitalSort => ({
    column: this.sortBy() || '',
    direction: this.sortDirection() ?? null,
  }));

  readonly target = '_self' as const;
  readonly createProductLink = computed(() => this.createLink('products'));
  readonly createBundleLink = computed(() => this.createLink('bundles'));

  private lastSelectAllValue = false;
  private syncingSelection = false;
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly store = inject(Store);
  private readonly catalogApi = inject(CatalogApiService);
  private readonly organizationId = signal<string | undefined>(undefined);
  private loadedCatalogOptionsOrgId: string | undefined;

  private get selectAllControl(): FormControl<boolean> {
    return this.form.controls['selectAll'];
  }

  private createLink(type: 'products' | 'bundles'): string[] | null {
    const organizationId = this.organizationId();
    return organizationId ? ['/org', organizationId, 'commerce', type, 'new'] : null;
  }

  constructor() {
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.handleSelectionChange());
    this.store
      .select(selectSelectedOrganizationId)
      .pipe(takeUntilDestroyed())
      .subscribe(organizationId => this.organizationId.set(organizationId));

    effect(() => {
      this.syncSelectionForm(this.selectable() ? this.products() || [] : [], this.selectedRowIds());
    });

    effect(onCleanup => {
      const organizationId = this.organizationId();

      if (this.isCatalogMode() || !organizationId) {
        this.loadedCatalogOptionsOrgId = undefined;
        this.catalogFilterCatalogCount.set(0);
        this.catalogFilterOptions.set(this.buildCatalogFilterOptions([]));
        return;
      }

      if (this.loadedCatalogOptionsOrgId === organizationId) {
        return;
      }

      const subscription = this.catalogApi
        .getAllCatalogsByOrganizationId(organizationId)
        .pipe(catchError(() => of([])))
        .subscribe(catalogs => {
          this.loadedCatalogOptionsOrgId = organizationId;
          this.catalogFilterCatalogCount.set(catalogs.length);
          this.catalogFilterOptions.set(this.buildCatalogFilterOptions(catalogs));
        });

      onCleanup(() => subscription.unsubscribe());
    });
  }

  @HostListener('document:click', ['$event'])
  documentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    const filterMenu = this.elementRef.nativeElement.querySelector('.product-table-filter-menu');
    const filterMenuButton = this.elementRef.nativeElement.querySelector('.product-table-filter-button');

    if (!this.filterMenuOpen() || !target || filterMenu?.contains(target) || filterMenuButton?.contains(target)) {
      return;
    }

    this.closeFilterMenu();
  }

  toggleFilterMenu(): void {
    this.filterMenuOpen.update(open => !open);
  }

  closeFilterMenu(): void {
    this.filterMenuOpen.set(false);
  }

  handleFiltersApplied(queryParams: ProductQueryParams): void {
    this.queryChanged.emit(queryParams);
    this.closeFilterMenu();
  }

  handleSearch(queryString: string): void {
    const queryParams: ProductQueryParams = {
      page: 1,
      query: queryString.trim(),
    };
    this.queryChanged.emit(queryParams);
  }

  paginationChange(event: SeFePaginationChange): void {
    const queryParams: ProductQueryParams = {
      page: event.currentPage,
      take: event.limit,
    };

    // Try to get current sort state from the sorter directive if available
    try {
      const currentSorter = this.sorter();
      const currentSortColumn = currentSorter?.active;
      const currentSortDirection = currentSorter?.direction;

      // Preserve current sort state if it exists
      if (currentSortColumn && currentSortDirection && currentSortDirection !== '') {
        queryParams.sortBy = currentSortColumn;
        queryParams.direction = currentSortDirection as SortDirection;
      }
    } catch {
      // Sorter might not be available in test environment or during initialization
      // Continue without sort preservation in this case
    }

    this.queryChanged.emit(queryParams);
  }

  sortChange(event: SortChangeEvent): void {
    if (!event || event.initial) return;

    // Preserve page size when sorting, but restart from the first page.
    const currentPaginationOptions = this.paginationOptions();
    const currentLimit = currentPaginationOptions?.limit || 10;

    let queryParams: ProductQueryParams = {
      page: 1,
      take: currentLimit, // Preserve current page size
    };

    if (event.direction) {
      queryParams = {...queryParams, sortBy: event.column, direction: event.direction as SortDirection};
    } else {
      queryParams = {...queryParams, sortBy: undefined, direction: undefined};
    }

    this.queryChanged.emit(queryParams);
  }

  /**
   * Gets the price for a product in dollars (same unit as product detail).
   * Uses the standalone product price; bundle detail rows display bundled price in a separate column.
   * @param product The product to get the price for.
   * @returns The price in dollars.
   */
  getProductPrice(product: ProductTableRow): number {
    return getTableRowPrice(product) ?? 0;
  }

  getRowId(product: ProductTableRow): string {
    return getTableRowId(product) || '';
  }

  getProductLink(product: ProductTableRow): string | undefined {
    const rowId = this.getRowId(product);
    if (!rowId) {
      return undefined;
    }

    if (this.isCatalogMode()) {
      return this.linkPrefix() + rowId;
    }

    if (this.isBundleRow(product)) {
      return this.mode() === 'bundleList' ? `./${rowId}` : `../bundles/${rowId}`;
    }

    return this.linkPrefix() + rowId;
  }

  isBundleRow(product: ProductTableRow): boolean {
    return this.mode() === 'bundleList' || isBundleProduct(product as Product);
  }

  deleteProductRow(product: ProductTableRow): void {
    this.deleteProduct.emit(product as Product);
  }

  removeProductRow(product: ProductTableRow): void {
    this.removeProduct.emit(product);
  }

  private handleSelectionChange(): void {
    if (this.syncingSelection) return;

    const selectAllValue = this.selectAllControl.value;
    if (selectAllValue !== this.lastSelectAllValue) {
      this.setProductControlValues(selectAllValue);
    } else {
      this.syncSelectAllControl();
    }

    this.lastSelectAllValue = this.selectAllControl.value;
    this.selectionChanged.emit({
      currentPageIds: this.currentPageRowIds(),
      selectedRows: this.selectedRowsFromForm(),
    });
  }

  private syncSelectionForm(products: ProductTableRow[], selectedRowIds: Set<string>): void {
    this.syncingSelection = true;

    const productIds = products.map(product => this.getRowId(product)).filter(Boolean);
    Object.keys(this.form.controls).forEach(controlName => {
      if (controlName !== 'selectAll' && !productIds.includes(controlName)) {
        this.form.removeControl(controlName, {emitEvent: false});
      }
    });

    productIds.forEach(productId => {
      if (!this.form.get(productId)) {
        this.form.addControl(productId, new FormControl(false, {nonNullable: true}), {emitEvent: false});
      }
      this.form.get(productId)?.setValue(selectedRowIds.has(productId), {emitEvent: false});
    });

    this.syncSelectAllControl();
    this.lastSelectAllValue = this.selectAllControl.value;
    this.syncingSelection = false;
  }

  private setProductControlValues(value: boolean): void {
    Object.keys(this.form.controls).forEach(controlName => {
      if (controlName !== 'selectAll') {
        this.form.get(controlName)?.setValue(value, {emitEvent: false});
      }
    });
  }

  private syncSelectAllControl(): void {
    const products = this.products() || [];
    const allSelected =
      products.length > 0 && products.every(product => this.form.get(this.getRowId(product))?.value === true);
    this.selectAllControl.setValue(allSelected, {emitEvent: false});
  }

  private selectedRowsFromForm(): ProductTableRow[] {
    return (this.products() || []).filter(product => this.form.get(this.getRowId(product))?.value === true);
  }

  private currentPageRowIds(): string[] {
    return (this.products() || []).map(product => this.getRowId(product)).filter(Boolean);
  }

  private buildCatalogFilterOptions(catalogs: CatalogSummary[]): SeFeSelectOptions {
    return {
      sections: [
        {
          options: [
            {text: 'All Catalogs', value: ''},
            ...catalogs.map(catalog => ({text: catalog.name, value: String(catalog.id)})),
          ],
        },
      ],
    };
  }
}
